import classNames from 'classnames';
import { motion } from 'framer-motion';
import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import RichText from '@/components/atoms/rich-text/rich-text';
import TripRouteAccordionHeadline from '@/components/modules/trip-route-details/trip-route-accordion-headline/trip-route-accordion-headline';
import ImageSlider from '@/components/organisms/image-slider/image-slider';
import styles from './trip-route-accordion-content.module.scss';

export interface TripRouteAccordionContentProps {
  /** The class name to apply */
  className?: string;
  /** Indicates whether the accordion is open */
  isActive?: boolean;
  /** The accordion item */
  item: Content.Properties.DescriptionItem;
  /** The identification for the accordion */
  itemIndex?: number;
  /** An event that is triggered on click */
  onClick?: () => void;
  /** The identification for the sub item in the block */
  subItemIndex?: number;
}

const TripRouteAccordionContent = ({
  className,
  isActive,
  item,
  itemIndex,
  onClick,
  subItemIndex,
}: TripRouteAccordionContentProps) => {
  const idSuffix = `${typeof subItemIndex !== 'undefined' ? `-${subItemIndex}` : ''}`;
  const accordionButtonId = `accordion-button-${itemIndex}${idSuffix}`;
  const accordionSectionId = `accordion-section-${itemIndex}${idSuffix}`;

  return (
    <div className={classNames(styles.component, className)}>
      <button
        aria-controls={accordionSectionId}
        aria-expanded={isActive}
        className={styles.header}
        id={accordionButtonId}
        onClick={onClick}
      >
        <div className={styles.headerTextWrapper}>
          <TripRouteAccordionHeadline
            headline={item.headline}
            subHeadline={item.subHeadline}
          />
        </div>

        <div aria-hidden="true" className={styles.accordionButton}>
          <Icon
            className={classNames(styles.accordionButtonIcon, {
              [styles.isIconActive]: isActive,
            })}
            name="ChevronDown"
          />
        </div>
      </button>

      <motion.div
        animate={{
          height: isActive ? 'auto' : 0,
        }}
        className={styles.itemFooter}
        initial={false}
      >
        <section
          aria-labelledby={accordionButtonId}
          className={styles.itemFooterContent}
          id={accordionSectionId}
        >
          {item.text && <RichText text={item.text} />}

          {item.images && item.images.length > 0 && (
            <ImageSlider className={styles.imageSlider} images={item.images} />
          )}
        </section>
      </motion.div>
    </div>
  );
};

TripRouteAccordionContent.displayName = 'TripRouteAccordionContent';

export default TripRouteAccordionContent;
